import { HttpEventType } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { saveAs } from 'file-saver';
import { DataNode, DataService } from 'ngx-explorer';
import { EMPTY, Observable, catchError, filter, map, of, tap } from 'rxjs';
import { DocumentService } from 'src/app/services/document.service';
import { LoadingService } from 'src/app/services/loading.service';
import { ToastService } from 'src/app/services/toast.service';
import { IDocumentItem } from 'src/app/shared/interfaces/IDocument';

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'ico', 'tiff', 'heic'];
const VIDEO_EXTENSIONS = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv', 'm4v', 'flv', 'wmv'];

function isFileMatchingFilter(file: IDocumentItem, filterType: string): boolean {
  if (file.isDirectory) return false;
  if (!filterType || filterType === 'all' || filterType === 'attach') return true;

  const dot = file.name.lastIndexOf('.');
  const ext = dot !== -1 ? file.name.substring(dot + 1).toLowerCase() : '';
  const contentType = file.contentType?.toLowerCase() || '';

  if (filterType === 'image') {
    return contentType.startsWith('image/') || IMAGE_EXTENSIONS.includes(ext);
  }
  if (filterType === 'video') {
    return contentType.startsWith('video/') || VIDEO_EXTENSIONS.includes(ext);
  }
  return true;
}

@Injectable()
export class DocumentDataService extends DataService {
  filterType: string = 'all';

  constructor(
    private documentService: DocumentService,
    private loadingService: LoadingService,
    private toastService: ToastService,
    private translateService: TranslateService
  ) {
    super();
  }

  getContent(data: IDocumentItem): Observable<{ files: IDocumentItem[]; dirs: IDocumentItem[] }> {
    return this.withLoading(
      this.documentService.getDocuments(data.path ?? null).pipe(
        map((res: { files: IDocumentItem[]; dirs: IDocumentItem[] }) => ({
          dirs: res.dirs ?? [],
          files: (res.files ?? []).filter((file) => isFileMatchingFilter(file, this.filterType))
        }))
      )
    );
  }

  createDir(data: IDocumentItem, name: string): Observable<IDocumentItem> {
    return this.withLoading(this.documentService.createDocumentDir(data.path ?? null, name));
  }

  rename(data: IDocumentItem, newName: string): Observable<IDocumentItem> {
    return this.withLoading(this.documentService.renameDocument(data.path!, !!data.isDirectory, newName));
  }

  delete(data: IDocumentItem[]): Observable<any> {
    return this.withLoading(this.documentService.deleteDocuments(data.map((item) => ({ path: item.path!, isDirectory: !!item.isDirectory }))));
  }

  uploadFiles(data: IDocumentItem, files: FileList): Observable<any> {
    return this.withLoading(this.documentService.uploadDocuments(data.path ?? null, files));
  }

  downloadFile(data: IDocumentItem): Observable<IDocumentItem> {
    return this.withLoading(
      this.documentService.downloadDocument(data.path!).pipe(
        filter((event: any) => event.type === HttpEventType.Response),
        map((event: any) => {
          saveAs(event.body, data.name);
          return data;
        })
      )
    );
  }

  getName(data: IDocumentItem): string {
    return data.name;
  }

  // Required by DataService's abstract contract, but nothing in this app ever calls
  // explorerService.openTree() - there's no "jump to a known deep path" feature here, so this
  // is never actually invoked. Kept as a no-op stub purely to satisfy the override.
  openTree(_data: IDocumentItem): Observable<Array<DataNode<IDocumentItem>>> {
    return of([]);
  }

  private withLoading<T>(source: Observable<T>): Observable<T> {
    return source.pipe(
      tap({
        subscribe: () => this.loadingService.show(),
        finalize: () => this.loadingService.hide()
      }),
      catchError(({ error }) => {
        this.toastService.error(this.translateService.instant(error.Message));
        return EMPTY;
      })
    );
  }
}
